import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button } from '@chakra-ui/react';
import { useHistory, useLocation } from 'react-router-dom';
import '../utils/default.css';
import SliderDisplay from './Components/SliderDisplay/SliderDisplay';
import Slider from './Components/Slider/Slider';
import AdminState from './Components/AdminState';
import ErrorBoundary from './Components/Tabs/ErrorBoundary';
import Backend from '../utils/api';
import useAdminFeedback from '../hooks/useAdminFeedback';
import { resolveAdminRoute, canonicalAdminTrail, destinationForTab } from './adminRoutes';

export default function Admin({ user }) {
  const history = useHistory();
  const location = useLocation();
  const destination = resolveAdminRoute(location.pathname);
  const principal = `${user.UID}:${Boolean(user.isSuperAdmin)}`;
  const [loaded, setLoaded] = useState(null);
  const [retry, setRetry] = useState(0);
  const [isAdmin, setIsAdmin] = useState(Boolean(user.isSuperAdmin));
  useAdminFeedback(user, isAdmin);

  useEffect(() => {
    let active = true;
    const complete = value => { if (active) setLoaded({ path: location.pathname, principal, ...value }); };
    if (!destination) {
      complete({ error: 'This admin page does not exist.', status: 'missing' });
      return () => { active = false; };
    }
    if (destination.path !== location.pathname) {
      history.replace({ ...location, pathname: destination.path });
      return () => { active = false; };
    }
    const { route, params } = destination;
    const load = async () => {
      try {
        const permissions = user.isSuperAdmin ? [] : await Backend.getLabPermissionsByUser(user.UID).then(response => {
          if (response.status !== 200) throw new Error('load');
          return (response.items || []).filter(item => item.is_admin);
        });
        if (active) setIsAdmin(user.isSuperAdmin || permissions.length > 0);
        if ((!user.isSuperAdmin && !permissions.length) || (route.superAdmin && !user.isSuperAdmin)) {
          complete({ status: 'forbidden', error: 'You do not have permission to open this admin page.' });
          return;
        }
        const props = {};
        if (params.space) {
          const spaces = await Backend.getLabs(1000);
          if (spaces.status !== 200) throw new Error('load');
          const space = (spaces.items || []).find(item => String(item.id).toLowerCase() === params.space);
          if (!space) {
            complete({ status: 'missing', error: 'This space could not be found. Choose a space from the admin home page.' });
            return;
          }
          if (!user.isSuperAdmin && !permissions.some(item => String(item.lab).toLowerCase() === String(space.id).toLowerCase())) {
            complete({ status: 'forbidden', error: 'You do not have admin access to this space.' });
            return;
          }
          const response = await Backend.getLabById({ id: space.id, UID: user.UID });
          if (response.status !== 200) throw new Error('load');
          props.labId = space.id;
          props.badgrState = response.badgrIntegration ? 'True' : 'False';
          if (route.id === 'settings') props.loadedSpace = response;
        }
        if (params.targetUid) {
          const response = await Backend.adminGetUserDetails(user.UID, Number(params.targetUid), props.labId);
          if (response.status !== 200) {
            complete({ status: response.status === 404 ? 'missing' : 'load', error: 'This user could not be loaded.' });
            return;
          }
          props.targetUser = { ...(response.data || response), UID: Number(params.targetUid) };
        }
        if (params.machineId) {
          const response = await Backend.getMachineById(user.UID, Number(params.machineId));
          if (response.status !== 200 || String(response.lab).toLowerCase() !== String(props.labId).toLowerCase()) {
            complete({ status: response.status === 200 || response.status === 404 ? 'missing' : 'load', error: 'This tool instance could not be found in the selected space.' });
            return;
          }
          props.machineId = Number(params.machineId);
        }
        complete({ props });
      } catch {
        complete({ status: 'load', error: 'The admin page could not be loaded. Try again.' });
      }
    };
    load();
    return () => { active = false; };
  // The pathname is the source of truth; never replay elements or callbacks from history state.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, user.UID, user.isSuperAdmin, retry]);

  const trail = destination ? canonicalAdminTrail(destination.route, destination.params) : ['/admin'];
  const storedTrail = location.state?.adminTrail;
  const navigationTrail = Array.isArray(storedTrail) && storedTrail.every(path => typeof path === 'string' && resolveAdminRoute(path)) ? storedTrail : trail;
  const pushTab = element => {
    const pathname = destinationForTab(element, destination?.params);
    history.push(pathname, { adminTrail: [...navigationTrail, location.pathname] });
  };
  const revert = (steps = 1) => {
    const count = Math.max(1, Number(steps) || 1);
    const remaining = navigationTrail.slice(0, Math.max(0, navigationTrail.length - count));
    const pathname = navigationTrail[Math.max(0, navigationTrail.length - count)] || '/admin';
    // Canonical parents also work when this tab was entered directly or reloaded.
    history.push(pathname, { adminTrail: remaining });
  };

  let content;
  if (!loaded || loaded.path !== location.pathname || loaded.principal !== principal) {
    content = <Slider name="Admin" index={0} slide preserveHeader onClick={() => history.push('/admin')}>
      <AdminState status="loading" title="Loading admin page…" />
    </Slider>;
  } else if (loaded.error) {
    content = <Slider name="Admin" index={0} slide onClick={() => history.push('/admin')}>
      <AdminState status="error" title={loaded.status === 'forbidden' ? 'Admin access required' : 'Admin page unavailable'}
        description={loaded.error} onRetry={loaded.status === 'load' ? () => setRetry(value => value + 1) : undefined}
        action={loaded.status !== 'load' ? <Button variant="outline" onClick={() => history.push('/admin')}>Choose a space</Button> : undefined} />
    </Slider>;
  } else {
    const View = destination.route.component;
    content = <ErrorBoundary key={location.pathname}><View {...loaded.props} user={user} pushTab={pushTab} revert={revert}
      index={destination.route.id === 'home' ? -1 : 0} setCurrentBadgrState={() => {}} onLabChanged={() => {}} /></ErrorBoundary>;
  }
  return <SliderDisplay persistentHeader>{content}</SliderDisplay>;
}

Admin.propTypes = { user: PropTypes.shape({ UID: PropTypes.number.isRequired, isSuperAdmin: PropTypes.bool }).isRequired };
